import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, ShareNetwork, Tray, WarningCircle } from '@phosphor-icons/react';

import { MarketShell } from '../../../design-system/components/shells';
import {
  Button,
  Empty,
  LoadingBlock,
  Pagination,
  Pill,
  QtyStepper,
  Select,
  Skeleton,
  Table,
} from '../../../design-system/components/desktop';
import {
  Cta,
  ListingRow,
  QtyStepper as MobileQtyStepper,
  Sheet,
  SheetBody,
  SheetHeader,
} from '../../../design-system/components/mobile';
import { Icon } from '../../../design-system/components/Icon';
import { useViewport } from '../../../design-system/hooks/useViewport';
import { ListingCard, ListingGrid } from '../../../design-system/patterns/market';
import {
  CtaNote,
  CtaStack,
  DetailAside,
  DetailCol,
  DetailLayout,
  DetailSection,
  DetailSkeleton,
  DetailSortBar,
  DetailToolbar,
  FieldLabel,
  Gallery,
  Identity,
  LowestPill,
  MarketLine,
  PriceBox,
  PriceHistory,
  PriceLead,
  Prose,
  RowActions,
  SectionHead,
  SellerCell,
  SpecList,
  TrustRows,
} from '../../../design-system/patterns/detail';
import {
  CONDITION_RANK,
  matrixAmount,
  matrixConditionOptions,
  matrixFinishOptions,
  pairFromAnchor,
} from '../../../design-system/patterns/detail/series';
import MarketplaceSorting from '../components/MarketplaceSorting/MarketplaceSorting';
import { formatCurrency } from '../../../utils/common';
import { distinguishingFinish, finishLabel } from '../../../utils/finish';
import { errorText, LOAD_FAILURE } from '../../../utils/errorText';
import {
  isPriceUnavailable,
  isPriceUnconfirmed,
  PRICE_UNCONFIRMED_TILE_NOTE,
} from '../../../utils/priceAvailability';

/**
 * The CATALOG page, `/marketplace/card/:cardId`. Pure view.
 *
 * WHAT THIS PAGE IS, AND WHAT IT IS NOT
 * -------------------------------------
 * It is the page about a CARD, not about a copy of it. Twelve sellers may have
 * the same Charizard at twelve prices in five conditions, so this page cannot
 * close a sale and must not pretend to: there is NO BUY BOX here. The right
 * column is price CONTEXT, and its primary action is honest about what it
 * does, it takes the buyer to the table where the choice actually gets made.
 *
 * The sibling route `/marketplace/card/:cardId/:listId` is one seller's
 * listing and that IS the page with the buy box. The two used to be
 * near-duplicate trees sharing global class names; they are now two pages with
 * two jobs sharing one `patterns/detail` scaffold.
 *
 * CHOOSING AMONG SELLERS IS A TABLE
 * ---------------------------------
 * "Browse and desire, use cards. Own and manage, use tables. Choose among
 * sellers, use tables." A grid of seller cards makes twelve near-identical
 * tiles the buyer has to diff by eye. A table puts condition, quantity and
 * price in aligned columns, which is the comparison they came to make. Below
 * 1024 that becomes stacked rows, which is the mobile row of the same map, not
 * a table crushed onto a phone.
 *
 * THE ONLY CARDS ON THIS PAGE ARE THE RELATED RAIL, where browsing is the job,
 * and they are the shared `ListingCard` rather than a seventh implementation.
 *
 * Every price is a RAND. `formatCurrency` is the only thing that renders money
 * and it prefixes `R`.
 */

const SORTS = [
  { value: 'price-asc', label: 'Price low to high' },
  { value: 'price-desc', label: 'Price high to low' },
  { value: 'rating', label: 'Seller rating' },
  { value: 'condition', label: 'Condition' },
];

// `Select` compares on strings, so these carry the label they render and the
// page casts the chosen value back to a number.
//
// DESKTOP ONLY, AND DELIBERATELY SO. Per page is a control for a viewport that
// can show the difference: at 1440 a row is 56px, so 10, 25 and 50 are three
// genuinely different amounts of scrolling. On a 390px phone a `ListingRow` is
// ~132px with its stepper and two CTAs, so 10 is already ~1,320px — over three
// screens — and 25 is eight. There is no reading of "50 per page" on a phone
// that is not just "the pager stops helping"; the control would be symmetry
// with the desktop toolbar and nothing else, and it would cost a second pill on
// the one line the sort trigger needs. Mobile stays on the 10 default and keeps
// the `Pagination` at the foot of the list, so the list is PAGED, never
// truncated: every seller is still reachable, ten at a time.
const PER_PAGE = [10, 25, 50];

const LISTINGS_ID = 'all-listings';

function conditionRank(condition) {
  const index = CONDITION_RANK.indexOf(condition);
  return index === -1 ? CONDITION_RANK.length : index;
}

export function sortListings(listings, sort) {
  const rows = [...listings];
  switch (sort) {
    case 'price-desc':
      return rows.sort((a, b) => b.price - a.price);
    case 'rating':
      return rows.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    case 'condition':
      return rows.sort((a, b) => conditionRank(a.condition) - conditionRank(b.condition));
    case 'price-asc':
    default:
      return rows.sort((a, b) => a.price - b.price);
  }
}

/**
 * THE PRICE, WITH THE CAVEAT THIS PAGE USED TO OMIT.
 *
 * Every listing row here has an Add to cart that goes through `useAddToCart`,
 * so this page fires the price-confirmation flow -- and it printed a bare
 * figure, which meant the buyer met that dialog with no warning at all. Marker
 * coverage has to equal flow coverage; a surface that fires the flow while
 * showing nothing is the exact defect two earlier designs were refused over.
 *
 * IT IS THE UNION, the same one the store tile (`MarketplaceGrid`) and the
 * entry gate read. `isPriceUnconfirmed` (nobody attempted this card today) and
 * `isPriceUnavailable` (nothing retrieved inside the sellability horizon) key
 * on different server fields and neither set contains the other, so either one
 * alone leaves rows marked-but-freely-addable or addable-but-unmarked.
 *
 * ONE TREATMENT, NOT TWO. The words and the class are the store tile's --
 * `PRICE_UNCONFIRMED_TILE_NOTE` and `.dsa-listing__pricenote` -- so what this
 * state LOOKS like stays a single design-system decision, still open and still
 * deliberately not settled from the API shape. The figure is never replaced,
 * only qualified. `--stacked` is layout and nothing else: on a tile the note
 * already lands on its own line inside a flex price row, while both surfaces
 * here put the price in a plain inline cell (the desktop table's `.is-num` td,
 * `.ds-listing-row__price` on mobile) where the note would otherwise sit
 * INLINE AFTER the figure -- "R120.00 Price unconfirmed" on one line, on both
 * of them. `display: block` is the half that fixes that; the modifier's
 * `white-space: normal` matters only on mobile, whose price cell is `nowrap`.
 *
 * NOT ON THE BUY BOX (`/marketplace/card/:cardId/:listId`). That surface has
 * its own cart-write path and does not fire this flow, so a marker there would
 * be one the buyer cannot act on. It gets the marker in the same change that
 * gives it the flow, or not at all.
 */
function priceCell(row, pending) {
  if (pending) return <Skeleton width="4.5rem" />;

  const figure = formatCurrency(row.price);
  if (!isPriceUnconfirmed(row) && !isPriceUnavailable(row)) return figure;

  return (
    <>
      {figure}
      {/* After the figure, never instead of it: "R120.00, Price unconfirmed"
          is a number carrying a caveat, where the reverse reads as a heading
          over a number. */}
      <span className="dsa-listing__pricenote dsa-listing__pricenote--stacked">
        {PRICE_UNCONFIRMED_TILE_NOTE}
      </span>
    </>
  );
}

function scrollToListings() {
  const target = document.getElementById(LISTINGS_ID);
  if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export function CardCatalogView({
  loading = false,
  error,
  notFound = false,

  card = {},
  priceHistory,
  priceMatrix = [],
  anchor = null,
  /* ONE FLAG, SIX SURFACES. Set by `CardDetail.jsx`'s refresh effect: false
     for the first 150ms (most refreshes finish inside that window and never
     show a loader at all), true from there until the refresh settles or 10s
     passes, whichever comes first. Every surface that renders a price reads
     this SAME flag, so they spin and resolve together -- a page mixing a
     spinning chart with a stale headline is the exact inconsistency this
     prop exists to prevent. */
  pricesPending = false,

  listings = [],
  listingsLoading = false,
  cartQuantities = {},
  addingIds = {},
  onAddToCart,
  onOpenListing,
  /* The listing's URL, not just a handler. The mobile row navigates with a real
     <a> so middle-click and "open in new tab" behave, and only the container
     knows how to build the path — same division as `onOpenListing`, which the
     desktop table still uses because `Table` takes a callback. */
  listingHref,

  saved = false,
  saving = false,
  onToggleSave,
  onShare,
  shareModal,

  related = [],
  relatedTitle = 'Customers also purchased',
  onSearch,
  onCartClick,
  wishlistCount = 0,

  /* An explicit way back, for when the crumb's parent is not where the buyer
     came from. See `CardDetail.jsx`. */
  backTo,
  backToLabel,
  /* THE SAME ORIGIN, PASSED ON DOWN. A listing sits one level under this page,
     and its own way back is here -- so unless this page hands the origin over
     on the way down, the trip back up arrives with the origin forgotten and
     the search is lost one hop later than it used to be. The handler-based
     paths (the desktop table row, the mobile open button) attach this in the
     container's `onOpenListing`; the stretched link on the mobile row is a
     `<Link>` and takes it here. */
  listingState,
  /* The origin handed to a SELLER STORE opened from the listing table, so its
     Back returns to this card instead of the marketplace. Separate from
     `listingState`, which is this page's OWN origin passed further down. */
  storeState,
  /* Positive knowledge that Sleevr can actually deliver, from
     `useFulfilmentMode` in the container. Defaults FALSE so the buy box makes
     no delivery promise it has not been told is true — the endpoint behind it
     is IsAuthenticated and this page is public. See `TrustRows`. */
  shipsNationwide = false,
}) {
  const { isDesktop } = useViewport();
  const [sort, setSort] = useState('price-asc');
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  // The mobile sort sheet. Mounted on the mobile track only, but the flag is
  // declared with the other hooks for the same reason `rowQty` is: this
  // component returns early below and a hook behind a branch changes the hook
  // count between renders.
  const [sortOpen, setSortOpen] = useState(false);
  const [imageIndex, setImageIndex] = useState(0);
  // Per-row add-to-cart quantity. Declared up here with the other hooks, not
  // beside the table column that uses it: this component returns early for the
  // loading and not-found states below, so a hook declared after those returns
  // is skipped on some renders and React sees the hook count change.
  const [rowQty, setRowQty] = useState({});

  /* THE PAGE OWNS THE FINISH/CONDITION SELECTION, not the chart.
     ------------------------------------------------------------------
     It used to live inside `PriceHistory`, which is why the market price above
     it could not follow it and always showed the best grade. Two surfaces read
     one selection now, so they cannot disagree.

     OPTIONS COME FROM THE PRICE MATRIX, NOT FROM THE HISTORY. Every option has
     to have a price, because selecting one sets the headline; 8% of priced
     pairs have no series at all, and those must still be selectable. The
     reverse never happens -- a series always has a priced row behind it -- so
     there is no union to take and no "priced at nothing" state to render.

     THE DEFAULT IS THE SERVER'S OWN ANCHOR, so the page opens on exactly the
     figure `representative_price_zar()` produced and agrees with the browse
     tile the buyer clicked. Re-deriving that rule here would be a second
     implementation of it, free to drift. The fallback is used only when the
     anchor names something the matrix does not carry. */
  const [selection, setSelection] = useState(null);

  const finishOptions = matrixFinishOptions(priceMatrix);
  const fallbackFinish = finishOptions[0] ?? null;
  const fallback = {
    finish: fallbackFinish,
    condition: matrixConditionOptions(priceMatrix, fallbackFinish)[0] ?? null,
  };
  const anchored = pairFromAnchor(priceMatrix, anchor?.finish, anchor?.condition);
  const active = selection ?? anchored ?? fallback;

  const conditionOptions = matrixConditionOptions(priceMatrix, active.finish);
  const activeAmount = matrixAmount(priceMatrix, active.finish, active.condition);
  const basis = [active.finish, active.condition].filter(Boolean).join(' · ') || undefined;

  const inStock = useMemo(() => listings.filter((row) => row.quantity > 0), [listings]);
  const sorted = useMemo(() => sortListings(inStock, sort), [inStock, sort]);
  const pageCount = Math.max(1, Math.ceil(sorted.length / perPage));
  const current = Math.min(page, pageCount);
  const rows = useMemo(
    () => sorted.slice((current - 1) * perPage, current * perPage),
    [sorted, current, perPage]
  );

  // Sort is one piece of state serving both tracks, and changing it on either
  // has to return to page one: the buyer is now looking at a different first
  // row, and page four of the old order means nothing in the new one.
  const chooseSort = (next) => {
    setSort(next);
    setPage(1);
  };
  const activeSort = SORTS.find((option) => option.value === sort);

  /* WHY `LowestPill` AND THE DEFAULT SORT GET NO PENDING TREATMENT, DELIBERATELY.
     ------------------------------------------------------------------------------
     Six money surfaces spin together off `pricesPending` (the market price, the
     chart, the price lead, the table's Price column, the CTA and the section
     aside). These two derive from `listings` and are NOT in that set, which reads
     like an oversight and is not one.

     The refresh replaces `listings` WHOLESALE in a single `setListings` call
     (`CardDetail.jsx`), so there is no window in which some rows are re-priced and
     others are not. That is the harm the six-surface treatment exists to prevent,
     and it cannot occur here: a badge and a sort order derived from one atomic
     array are always self-consistent.

     What CAN happen is that the badge moves to a different row, or the table
     reorders, while a buyer is reading it. That is accepted. Treating it would
     mean two more surfaces to keep in step with the other six forever, for a
     reordering that is visibly a data update rather than a wrong number. */
  const lowest = inStock.length ? Math.min(...inStock.map((row) => row.price)) : null;
  const lowestId = lowest == null ? null : inStock.find((row) => row.price === lowest)?.id;

  const shell = {
    breadcrumbs: [
      { label: 'Marketplace', to: '/marketplace' },
      ...(card.set ? [{ label: card.set }] : []),
      { label: card.name || 'Card' },
    ],
    /* An explicit `back` wins over the one `MarketShell` derives from the trail
       above, which is what makes a card opened out of a search return to the
       search. The TRAIL is deliberately left alone: it names where this page
       sits in the catalogue, which is under Marketplace however you got here,
       and rewriting it per route of arrival would make the same card claim a
       different home each visit. */
    back: backTo ? { to: backTo, label: `Back to ${backToLabel || 'results'}` } : undefined,
    title: card.name,
    search: onSearch ? { onSubmit: onSearch } : undefined,
    onCartClick,
    wishlistCount,
    activeNav: 'marketplace',
  };

  if (loading) {
    // The seller table is the reason this page exists, so the loading shape
    // includes it. Five rows is the shortest block that still reads as a table.
    return (
      <MarketShell {...shell}>
        <DetailSkeleton listingRows={5} />
      </MarketShell>
    );
  }

  if (notFound || error) {
    return (
      <MarketShell {...shell}>
        <Empty
          icon={WarningCircle}
          illustration={notFound ? 'not-found' : 'request-failed'}
          tone={notFound ? 'default' : 'error'}
          title={notFound ? 'We could not find that card' : 'We could not load this card'}
          text={
            notFound
              ? 'The card may have been removed from the catalog, or the link may be wrong.'
              : errorText(error, LOAD_FAILURE)
          }
          actions={
            /* `as={Link} to=`, not `as="a" href=`: a plain anchor to an in-app
               route is a full page load, which tears down the React tree and
               refetches the bundle to reach a page the router already had. No
               `state` — this one goes to the marketplace, which is nobody's
               parent to remember. */
            <Button as={Link} to="/marketplace" variant="ghost">
              Back to marketplace
            </Button>
          }
        />
      </MarketShell>
    );
  }

  // No "Category" row: the catalog payload's only grouping field is
  // `catalog_group`, which is the SET and is already the first row. See the
  // note in CardDetail.jsx.
  const specs = [
    { label: 'Set', value: card.set },
    { label: 'Number', value: card.cardNumber },
    { label: 'Rarity', value: card.rarity },
    { label: 'Language', value: card.language },
  ];

  const qtyFor = (row, remaining) => Math.min(rowQty[row.id] ?? 1, Math.max(1, remaining));

  const columns = [
    {
      key: 'seller',
      header: 'Seller',
      render: (row) => (
        <SellerCell
          name={row.sellerName}
          rating={row.rating}
          sales={row.sales}
          badge={row.id === lowestId ? <LowestPill /> : null}
          to={row.sellerId ? `/seller/${row.sellerId}` : undefined}
          state={storeState}
        />
      ),
    },
    {
      key: 'condition',
      header: 'Condition',
      render: (row) => (row.condition ? <Pill fit>{row.condition}</Pill> : null),
    },
    /* FINISH, and it prints even when it is "Normal". The API has always sent
       this per listing and the page has never shown it, so a Reverse Holofoil
       sat in this table looking exactly like a Normal one. A column has a
       heading, and a blank cell under it reads as "we were not told" -- which
       is a different claim from "normal" and the wrong one to make about a
       field that is populated on every row. The dense surfaces below use
       `distinguishingFinish` instead; see utils/finish.js for why they differ. */
    {
      key: 'finish',
      header: 'Finish',
      render: (row) => {
        const label = finishLabel(row.finish);
        return label ? <Pill fit>{label}</Pill> : null;
      },
    },
    { key: 'quantity', header: 'Available', numeric: true },
    {
      key: 'price',
      header: 'Price',
      numeric: true,
      render: (row) => priceCell(row, pricesPending),
    },
    {
      key: 'action',
      header: '',
      // 260, not the old 150. Measured at 1440: the stepper is 118px
      // (36 + 44 + 36 plus its 1px borders), the gap is 8, and the `--sm`
      // button on its widest label, "Add to cart", is 98; the `td` adds
      // 2 x 16px of padding. 118 + 8 + 98 + 32 = 256, so 260 leaves the row a
      // little slack. At 150 there was nowhere near room for both, so the
      // button was squeezed and broke "Add to cart" onto a second line inside
      // a control whose height is a fixed 32px.
      width: 260,
      render: (row) => {
        const remaining = Math.max(0, row.quantity - (cartQuantities[row.id] || 0));
        const qty = qtyFor(row, remaining);
        return (
          <RowActions>
            <QtyStepper
              value={qty}
              min={1}
              max={Math.max(1, remaining)}
              disabled={remaining <= 0}
              onChange={(next) => setRowQty((prev) => ({ ...prev, [row.id]: next }))}
              onClick={(event) => event.stopPropagation()}
            />
            {/* Ink, not ghost. Adding to the cart is a committed command and
                every other commit moment in the marketplace is ink; a ghost
                button here read as the row's secondary affordance next to the
                stepper, when it is the one thing the row is for. */}
            <Button
              variant="primary"
              size="sm"
              disabled={addingIds[row.id] || remaining <= 0}
              onClick={(event) => {
                // The row itself opens the seller's listing, so the action
                // inside it has to stop there or every add is also a navigation.
                event.stopPropagation();
                if (onAddToCart) onAddToCart(row, qty);
              }}
            >
              {addingIds[row.id] ? 'Adding' : remaining <= 0 ? 'In cart' : 'Add to cart'}
            </Button>
          </RowActions>
        );
      },
    },
  ];

  const priceLead = (
    <div>
      {/* Words, not a dash, when there is no lowest listing. An em dash is
          banned product wide, and a lone dash where a price belongs reads as
          "free" or "zero" just as easily as "we do not know", which is the
          opposite of what it means. Matches formatCurrency for the same case. */}
      <PriceLead
        label="Lowest listing"
        price={lowest == null ? 'No price' : formatCurrency(lowest)}
        pending={pricesPending}
        sub={
          inStock.length
            ? `${inStock.length} ${inStock.length === 1 ? 'listing' : 'listings'} available`
            : 'No seller has this card in stock right now'
        }
      />
      {/* The card's story lives in "About this card" under the image, where it
          fills the short left column. Repeating it here would be the same
          paragraph twice on one screen, so this carries only what a price lead
          promises: what this card costs right now. */}
      {inStock.length > 1 ? (
        <>
          <div className="dsa-detail__field-label">Condition available</div>
          <div className="dsa-detail__lead-sub">
            {[...new Set(inStock.map((row) => row.condition))].join(' · ')}
          </div>
        </>
      ) : null}
    </div>
  );

  return (
    <MarketShell {...shell}>
      <DetailLayout>
        <DetailCol>
          <Gallery
            images={card.images || []}
            index={imageIndex}
            onSelect={setImageIndex}
            alt={card.name || 'Card'}
            badge={card.rarity}
          />

          {card.description ? (
            <section>
              <h2 className="dsk-section-title">About this card</h2>
              <Prose>
                <p>{card.description}</p>
              </Prose>
            </section>
          ) : null}
        </DetailCol>

        {/* BUY BOX ORDER IS FIXED: identity, market price, price box, CTA
            stack, then specs and buyer protection. The price box sits directly
            under the market price. Neither of those facts moves. */}
        <DetailAside>
          <Identity
            set={card.set}
            name={card.name}
            rarity={card.rarity}
            cardNumber={card.cardNumber}
          />

          <MarketLine
            value={activeAmount == null ? null : formatCurrency(activeAmount)}
            basis={basis}
            pending={pricesPending}
          />

          <PriceBox>
            {priceLead}
            {/* The chart lost its name when the tab labelled "Price history"
                went, so the name is printed above it. An unlabelled line of
                Rands over a date axis is a shape, not a fact. */}
            <div>
              <FieldLabel>Price history</FieldLabel>
              {/* The selectors live HERE now, rendered by the page, because
                  they drive the figure above as well as the chart below.
                  Guarded on `finishOptions.length`, the same guard
                  `PriceHistory` used to carry itself (its own copy, at
                  `finishOptions.length > 0` in PriceHistory.jsx): a card with
                  no priced rows at all has nothing to choose between, and two
                  empty drop-downs said otherwise. */}
              {finishOptions.length > 0 ? (
                <div className="dsa-chart__selectors">
                  <Select
                    label="Finish"
                    options={finishOptions.map((o) => ({ value: o, label: o }))}
                    value={active.finish ?? ''}
                    onChange={(next) => {
                      const conds = matrixConditionOptions(priceMatrix, next);
                      setSelection({
                        finish: next,
                        condition: conds.includes(active.condition)
                          ? active.condition
                          : (conds[0] ?? null),
                      });
                    }}
                  />
                  <Select
                    label="Condition"
                    options={conditionOptions.map((o) => ({ value: o, label: o }))}
                    value={active.condition ?? ''}
                    onChange={(next) => setSelection({ finish: active.finish, condition: next })}
                  />
                </div>
              ) : null}
              <PriceHistory
                marketHistory={priceHistory}
                finish={active.finish}
                condition={active.condition}
                height={200}
                formatValue={formatCurrency}
                pending={pricesPending}
              />
            </div>
          </PriceBox>

          <CtaStack>
            {/* This button cannot close the sale, because every copy belongs to
                a different seller at a different price and condition. So it
                says what it really does and delivers the buyer to the table. */}
            <Button
              size="lg"
              block
              disabled={!inStock.length || pricesPending}
              onClick={scrollToListings}
            >
              {pricesPending
                ? 'Checking prices'
                : lowest == null
                  ? 'No listings available'
                  : `Choose a listing, from ${formatCurrency(lowest)}`}
            </Button>

            <Button variant="ghost" size="lg" block disabled={saving} onClick={onToggleSave}>
              <Icon as={Heart} size="sm" active={saved} />
              {saved ? 'In your collection' : 'Add to collection'}
            </Button>

            {onShare ? (
              <Button variant="ghost" size="lg" block onClick={onShare}>
                <Icon as={ShareNetwork} size="sm" />
                Share
              </Button>
            ) : null}

            {inStock.length > 1 ? (
              <CtaNote>
                {inStock.length} sellers have this card. Compare condition, quantity and price
                below.
              </CtaNote>
            ) : null}
          </CtaStack>

          <SpecList items={specs} />
          <TrustRows shipsNationwide={shipsNationwide} />
        </DetailAside>
      </DetailLayout>

      <DetailSection id={LISTINGS_ID} aria-labelledby="all-listings-title">
        <SectionHead
          id="all-listings-title"
          title={`${inStock.length} ${inStock.length === 1 ? 'listing' : 'listings'}`}
          sub={
            inStock.length
              ? 'Every seller with this card in stock. Pick the one you want, then add it to your cart.'
              : 'No seller has this card in stock right now.'
          }
          aside={
            pricesPending ? (
              <div className="from">
                Lowest right now
                <b>
                  <Skeleton width="4rem" />
                </b>
              </div>
            ) : lowest == null ? null : (
              <div className="from">
                Lowest right now<b>{formatCurrency(lowest)}</b>
              </div>
            )
          }
        />

        {isDesktop ? (
          <Table
            columns={columns}
            data={rows}
            rowKey="id"
            loading={listingsLoading}
            onRowClick={onOpenListing ? (row) => onOpenListing(row) : undefined}
            emptyTitle="No seller has this card in stock"
            emptyText="Add it to your collection and we will keep it on your list."
            /* Sort and per page are the library `Select`, not raw `<select>`.
               A native select paints the OS popup, which honours none of our
               radius, type ramp or dark theme, and these two were the last
               controls on the page still doing it. `Select` keeps a real
               `<select>` in the DOM for form semantics, but it is `aria-hidden`
               and 1px, so the visible name has to come from the `label` prop;
               the text beside it is a plain span, because a `<label htmlFor>`
               pointing at a hidden control names nothing. `onChange` hands back
               the raw string, so per page casts. */
            toolbar={
              <DetailToolbar>
                <span className="lbl">Sort</span>
                <Select
                  className="dsa-toolbar-select"
                  label="Sort listings"
                  options={SORTS}
                  value={sort}
                  onChange={chooseSort}
                />
                <div className="spacer" />
                <span className="lbl">Per page</span>
                <Select
                  className="dsa-toolbar-select dsa-toolbar-select--sm"
                  label="Listings per page"
                  options={PER_PAGE}
                  value={perPage}
                  onChange={(next) => {
                    setPerPage(Number(next));
                    setPage(1);
                  }}
                />
              </DetailToolbar>
            }
            pagination={
              pageCount > 1 ? (
                <Pagination
                  page={current}
                  pageCount={pageCount}
                  onPageChange={setPage}
                  totalItems={sorted.length}
                  pageSize={perPage}
                  itemNoun="listings"
                />
              ) : null
            }
          />
        ) : (
          <div>
            {/* The mobile row of the same map: data as stacked rows, never a
                table squeezed onto a phone. Same data, same order, same
                actions.

                SORT IS PART OF "SAME ACTIONS", and it was missing. The desktop
                controls live in the `Table`'s `toolbar` prop above, so they
                exist only where the table does — a phone got the whole seller
                list in one fixed order with nothing to reorder it by, which on
                this page is the one thing the buyer came to do: cheapest first,
                or best condition first. Below the handoff that is a `Sheet`
                opened from a pill, the pattern MarketplaceHeader established, so
                the app has one sort control and not two.

                No per-page twin. The reasoning is on `PER_PAGE` above; the short
                of it is that mobile stays at 10 and keeps the `Pagination`
                below, so the list is paged, not cut off.

                Only above one listing: a sort control over a single seller is a
                control with nothing to do, and it disappears while the rows are
                loading rather than sitting live over a block that is about to
                be replaced. */}
            {!listingsLoading && inStock.length > 1 ? (
              <DetailSortBar
                value={activeSort ? activeSort.label : undefined}
                onOpen={() => setSortOpen(true)}
              />
            ) : null}

            {listingsLoading ? (
              <LoadingBlock>Loading listings</LoadingBlock>
            ) : rows.length === 0 ? (
              <Empty
                inline
                icon={Tray}
                title="No seller has this card in stock"
                text="Add it to your collection and we will keep it on your list."
              />
            ) : (
              rows.map((row) => {
                const remaining = Math.max(0, row.quantity - (cartQuantities[row.id] || 0));
                return (
                  <ListingRow
                    key={row.id}
                    store={row.sellerName}
                    /* The same two destinations the desktop table offers: the
                       store name goes to the shop, the row goes to the listing.
                       Both are links rather than handlers, so the row behaves
                       like every other navigation on the page. */
                    storeTo={row.sellerId ? `/seller/${row.sellerId}` : undefined}
                    storeState={storeState}
                    to={listingHref ? listingHref(row) : undefined}
                    toState={listingState}
                    openLabel={`View ${row.sellerName || 'seller'}'s listing`}
                    rating={row.rating == null ? undefined : row.rating.toFixed(1)}
                    price={priceCell(row, pricesPending)}
                    /* Both pills on one line, and the finish only when it
                       says something: this row is already carrying a store
                       name, a rating, a price and a stock count on a 390px
                       screen, and a "Normal" pill on nearly every row would
                       cost that space to say nothing. */
                    conditionTags={
                      <>
                        {row.condition ? <Pill fit>{row.condition}</Pill> : null}
                        {distinguishingFinish(row.finish) ? (
                          <Pill fit>{distinguishingFinish(row.finish)}</Pill>
                        ) : null}
                      </>
                    }
                    stock={`${row.quantity} available`}
                    actions={
                      <>
                        {/* The mobile stepper, not the desktop one. `.dsk-qty`
                            is 40px tall with 36px buttons; `.ds-qty-stepper`'s
                            are 44x44. This is the control a buyer hits
                            repeatedly before adding to cart, so it is the last
                            place to sit under the floor. `min`/`max` disable
                            the ends, which covers the sold-out case: at
                            remaining 0 both bounds collapse to 1.
                            patterns/cart/index.jsx:198 is the precedent. */}
                        <MobileQtyStepper
                          value={qtyFor(row, remaining)}
                          min={1}
                          max={Math.max(1, remaining)}
                          onDecrement={() =>
                            setRowQty((prev) => ({
                              ...prev,
                              [row.id]: qtyFor(row, remaining) - 1,
                            }))
                          }
                          onIncrement={() =>
                            setRowQty((prev) => ({
                              ...prev,
                              [row.id]: qtyFor(row, remaining) + 1,
                            }))
                          }
                        />
                        <Cta
                          variant="ghost"
                          size="sm"
                          onClick={() => onOpenListing && onOpenListing(row)}
                        >
                          View
                        </Cta>
                        <Cta
                          size="sm"
                          disabled={addingIds[row.id] || remaining <= 0}
                          onClick={() => onAddToCart && onAddToCart(row, qtyFor(row, remaining))}
                        >
                          {addingIds[row.id]
                            ? 'Adding'
                            : remaining <= 0
                              ? 'In cart'
                              : 'Add to cart'}
                        </Cta>
                      </>
                    }
                  />
                );
              })
            )}
            {/* This is why there is no per-page control below the handoff: the
                list is already paged at 10, so nothing is silently dropped. */}
            {pageCount > 1 ? (
              <Pagination
                page={current}
                pageCount={pageCount}
                onPageChange={setPage}
                totalItems={sorted.length}
                pageSize={perPage}
                itemNoun="listings"
              />
            ) : null}

            {/* Mounted whether or not it is open, and passed `open`. A Sheet
                written as `{sortOpen && <Sheet/>}` is torn out of the DOM on the
                tick it closes, so its slide-down has nothing to play on and it
                just vanishes; it holds itself in for one exit and then leaves.
                `MarketplaceSorting` is the same option list `/marketplace` and
                the seller store put in their sort sheets — mobile only by
                construction, it emits `ds-` and closes the sheet on choose,
                because the result set changes underneath and there is nothing
                left to decide. */}
            <Sheet open={sortOpen} onClose={() => setSortOpen(false)}>
              <SheetHeader title="Sort listings" />
              <SheetBody>
                <MarketplaceSorting
                  sortOptions={SORTS}
                  value={sort}
                  onSortChange={chooseSort}
                  onClose={() => setSortOpen(false)}
                />
              </SheetBody>
            </Sheet>
          </div>
        )}
      </DetailSection>

      {related.length > 0 ? (
        <DetailSection>
          <h2 className="dsk-section-title">{relatedTitle}</h2>
          <ListingGrid>
            {related.map((item) => (
              <ListingCard
                key={item.id}
                href={`/marketplace/card/${item.id}`}
                image={item.image}
                imageAlt={item.name}
                set={item.set}
                name={item.name}
                rarity={item.rarity}
                cardNumber={item.cardNumber}
                price={item.price == null ? undefined : formatCurrency(item.price)}
                marketPrice={
                  item.marketPrice == null ? undefined : formatCurrency(item.marketPrice)
                }
              />
            ))}
          </ListingGrid>
        </DetailSection>
      ) : null}

      {shareModal}
    </MarketShell>
  );
}

export default CardCatalogView;
